import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  TextField,
} from '@mui/material';
import { Question, QuestionType, SubjectEnum, Difficulty, SubQuestion } from '../types';
import UnifiedRenderer from './UnifiedRenderer';
import OptimizedMathRenderer from './OptimizedMathRenderer';

interface QuestionRendererProps {
  question: Question;
  showAnswer?: boolean;
  showExplanation?: boolean;
  interactive?: boolean;
  onAnswerChange?: (answer: any) => void;
  /** 是否在弹窗中渲染（使用高优先级渲染器） */
  inModal?: boolean;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  showAnswer = false,
  showExplanation = false,
  interactive = false,
  onAnswerChange,
  inModal = false,
}) => {
  const [userAnswer, setUserAnswer] = React.useState<any>('');

  // 数据验证和转换功能
  const validateAndNormalizeQuestion = React.useMemo(() => {
    if (!question || !question.body) {
      console.error('QuestionRenderer: 题目数据无效', question);
      return null;
    }

    // 确保body是对象
    let normalizedBody: any;
    if (typeof question.body === 'string') {
      try {
        normalizedBody = JSON.parse(question.body);
      } catch (e) {
        console.error('QuestionRenderer: 无法解析题目body JSON', e);
        return null;
      }
    } else {
      normalizedBody = { ...question.body };
    }

    // 验证必要字段
    if (!normalizedBody.type) {
      console.error('QuestionRenderer: 缺少题目类型', normalizedBody);
      return null;
    }

    if (!normalizedBody.content) {
      console.error('QuestionRenderer: 缺少题目内容', normalizedBody);
      return null;
    }

    return {
      ...question,
      body: normalizedBody
    };
  }, [question]);

  // 调试输出题目信息
  React.useEffect(() => {
    const validatedQuestion = validateAndNormalizeQuestion;
    if (validatedQuestion) {
      console.log('QuestionRenderer 题目渲染调试:', {
        题目ID: validatedQuestion.id,
        题目类型: validatedQuestion.body.type,
        学科: validatedQuestion.body.subject,
        难度: validatedQuestion.body.difficulty,
        题目内容预览: validatedQuestion.body.content?.substring(0, 100) + '...',
        选项数量: validatedQuestion.body.options?.length || 0,
        答案: validatedQuestion.body.answer,
        是否显示答案: showAnswer,
        是否显示解析: showExplanation,
        是否交互模式: interactive,
        数据结构: {
          bodyType: typeof validatedQuestion.body,
          hasContent: !!validatedQuestion.body.content,
          hasOptions: !!validatedQuestion.body.options,
          hasAnswer: !!validatedQuestion.body.answer
        }
      });
    } else {
      console.error('QuestionRenderer: 题目验证失败，无法渲染');
    }
  }, [validateAndNormalizeQuestion, showAnswer, showExplanation, interactive]);

  // 处理答案变化
  const handleAnswerChange = (answer: any) => {
    setUserAnswer(answer);
    onAnswerChange?.(answer);
  };

  // 获取题型标签
  const getQuestionTypeLabel = (type: QuestionType): string => {
    const labels: Record<QuestionType, string> = {
      SINGLE_CHOICE: '单选题',
      MULTIPLE_CHOICE: '多选题',
      FILL_IN_BLANK: '填空题',
      TRUE_FALSE: '判断题',
      MATCHING: '匹配题',
      READING_COMPREHENSION: '阅读理解',
      CLOZE_TEST: '完形填空',
      LISTENING: '听力题',
    };
    return labels[type] || type;
  };

  // 获取科目标签 - 支持中文和英文枚举值
  const getSubjectLabel = (subject: SubjectEnum | string): string => {
    // 如果已经是中文，直接返回
    if (typeof subject === 'string' && ['英语', '数学', '物理', '化学', '生物', '历史', '地理'].includes(subject)) {
      return subject;
    }
    
    // 英文枚举到中文的映射
    const labels: Record<string, string> = {
      ENGLISH: '英语',
      MATH: '数学',
      PHYSICS: '物理',
      CHEMISTRY: '化学',
      BIOLOGY: '生物',
      HISTORY: '历史',
      GEOGRAPHY: '地理',
      '英语': '英语',
      '数学': '数学', 
      '物理': '物理',
      '化学': '化学',
      '生物': '生物',
      '历史': '历史',
      '地理': '地理',
    };
    return labels[subject as string] || String(subject);
  };

  // 获取难度标签和颜色 - 支持中文和英文枚举值
  const getDifficultyInfo = (difficulty: Difficulty | string) => {
    // 处理中文直接映射
    const chineseMap: Record<string, { label: string; color: 'success' | 'warning' | 'error' }> = {
      '简单': { label: '简单', color: 'success' },
      '中等': { label: '中等', color: 'warning' },
      '困难': { label: '困难', color: 'error' },
    };
    
    if (typeof difficulty === 'string' && chineseMap[difficulty]) {
      return chineseMap[difficulty];
    }
    
    // 处理英文枚举
    const englishMap: Record<string, { label: string; color: 'success' | 'warning' | 'error' }> = {
      EASY: { label: '简单', color: 'success' },
      MEDIUM: { label: '中等', color: 'warning' },
      HARD: { label: '困难', color: 'error' },
    };
    
    return englishMap[difficulty as string] || { label: String(difficulty), color: 'warning' };
  };

  // 判断是否为嵌套题型
  const isNestedQuestion = (type: QuestionType): boolean => {
    return ['READING_COMPREHENSION', 'LISTENING', 'CLOZE_TEST'].includes(type);
  };

  // 渲染HTML内容（根据是否在弹窗中选择不同的渲染策略）
  const renderHtmlContent = (content: string) => {
    // 生产环境可以移除此调试输出
    if (process.env.NODE_ENV === 'development') {
      console.log('QuestionRenderer HTML内容渲染:', {
        内容长度: content.length,
        渲染策略: inModal ? '弹窗优化渲染' : '普通渲染',
        是否弹窗: inModal
      });
    }

    // 弹窗中使用优化的渲染器，提供更好的性能
    if (inModal) {
      return (
        <OptimizedMathRenderer
          content={content}
          priority="high"
          style={{
            lineHeight: 1.6,
            fontSize: '14px',
          }}
          onError={(error) => {
            console.error('QuestionRenderer 弹窗渲染错误:', error);
          }}
        />
      );
    }

    // 非弹窗环境继续使用UnifiedRenderer
    return (
      <UnifiedRenderer
        content={content}
        style={{
          lineHeight: 1.6,
          fontSize: '14px',
        }}
        onError={(error) => {
          console.error('QuestionRenderer 渲染错误:', error);
        }}
      />
    );
  };

  // 解析答案（处理JSON格式的答案）
  const parseAnswer = (answer: any) => {
    if (Array.isArray(answer)) {
      return answer;
    }

    if (typeof answer === 'string') {
      // 尝试解析JSON格式的答案
      try {
        const parsed = JSON.parse(answer);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        // 不是JSON格式，返回原值
      }
    }

    return answer;
  };

  // 渲染答案内容（根据是否在弹窗中选择不同的渲染策略）
  const renderAnswerContent = (answer: any) => {
    const answerText = String(answer);
    if (!answerText) return <span>-</span>;

    // 生产环境可以移除此调试输出
    if (process.env.NODE_ENV === 'development') {
      console.log('QuestionRenderer 答案渲染:', answerText.substring(0, 50));
    }

    try {
      // 弹窗中使用优化的渲染器
      if (inModal) {
        return (
          <OptimizedMathRenderer
            content={answerText}
            inline={true}
            priority="high"
            style={{
              fontSize: '14px',
              display: 'inline',
              whiteSpace: 'nowrap'
            }}
            onError={(error) => {
              console.warn('弹窗答案渲染失败，降级为纯文本显示:', error);
            }}
          />
        );
      }

      // 非弹窗环境使用UnifiedRenderer
      return (
        <UnifiedRenderer
          content={answerText}
          inline={true}
          style={{
            fontSize: '14px',
            display: 'inline',
            whiteSpace: 'nowrap'
          }}
          onError={(error) => {
            console.warn('答案渲染失败，降级为纯文本显示:', error);
          }}
        />
      );
    } catch (error) {
      console.warn('答案渲染异常，降级为纯文本显示:', error);
      return <span style={{ display: 'inline', whiteSpace: 'nowrap' }}>{answerText}</span>;
    }
  };

  // 渲染单选题
  const renderSingleChoice = () => (
    <Box>
      {renderHtmlContent(validatedQuestion.body.content)}
      
      {validatedQuestion.body.options && (
        <RadioGroup
          value={interactive ? userAnswer : ''}
          onChange={(e) => interactive && handleAnswerChange(e.target.value)}
          sx={{ mt: 2 }}
        >
          {validatedQuestion.body.options.map((option: string, index: number) => (
            <FormControlLabel
              key={index}
              value={String.fromCharCode(65 + index)}
              control={<Radio size="small" />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" component="span">
                    {String.fromCharCode(65 + index)}.
                  </Typography>
                  <div>{renderHtmlContent(option)}</div>
                  {showAnswer && workingQuestion.body.answer === String.fromCharCode(65 + index) && (
                    <Chip label="正确答案" size="small" color="success" />
                  )}
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1 }}
            />
          ))}
        </RadioGroup>
      )}
    </Box>
  );

  // 渲染多选题
  const renderMultipleChoice = () => (
    <Box>
      {renderHtmlContent(workingQuestion.body.content)}
      
      {workingQuestion.body.options && (
        <FormGroup sx={{ mt: 2 }}>
          {workingQuestion.body.options.map((option: string, index: number) => {
            const optionKey = String.fromCharCode(65 + index);
            const parsedAnswer = parseAnswer(workingQuestion.body.answer);
            const isCorrect = Array.isArray(parsedAnswer) &&
                             parsedAnswer.includes(optionKey);
            
            return (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    size="small"
                    checked={interactive ? (userAnswer || []).includes(optionKey) : false}
                    onChange={(e) => {
                      if (interactive) {
                        const currentAnswers = userAnswer || [];
                        const newAnswers = e.target.checked
                          ? [...currentAnswers, optionKey]
                          : currentAnswers.filter((a: string) => a !== optionKey);
                        handleAnswerChange(newAnswers);
                      }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" component="span">
                      {optionKey}.
                    </Typography>
                    <div>{renderHtmlContent(option)}</div>
                    {showAnswer && isCorrect && (
                      <Chip label="正确答案" size="small" color="success" />
                    )}
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
            );
          })}
        </FormGroup>
      )}
    </Box>
  );

  // 渲染填空题
  const renderFillInBlank = () => (
    <Box>
      {renderHtmlContent(workingQuestion.body.content)}
      
      {interactive && (
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="请输入答案"
          value={userAnswer}
          onChange={(e) => handleAnswerChange(e.target.value)}
          sx={{ mt: 2 }}
        />
      )}
      
      {showAnswer && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="success.main" fontWeight="bold">
            参考答案：
          </Typography>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            '& > span': {
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap'
            }
          }}>
            {(() => {
              const parsedAnswer = parseAnswer(workingQuestion.body.answer);
              return Array.isArray(parsedAnswer)
                ? parsedAnswer.map((ans, idx) => (
                    <span key={idx}>
                      {renderAnswerContent(ans)}
                      {idx < parsedAnswer.length - 1 && '、'}
                    </span>
                  ))
                : renderAnswerContent(parsedAnswer);
            })()}
          </Box>
        </Box>
      )}
    </Box>
  );

  // 渲染判断题
  const renderTrueFalse = () => (
    <Box>
      {renderHtmlContent(workingQuestion.body.content)}
      
      <RadioGroup
        value={interactive ? userAnswer : ''}
        onChange={(e) => interactive && handleAnswerChange(e.target.value === 'true')}
        sx={{ mt: 2 }}
      >
        <FormControlLabel
          value="true"
          control={<Radio size="small" />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">正确</Typography>
              {showAnswer && workingQuestion.body.answer === true && (
                <Chip label="正确答案" size="small" color="success" />
              )}
            </Box>
          }
        />
        <FormControlLabel
          value="false"
          control={<Radio size="small" />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">错误</Typography>
              {showAnswer && workingQuestion.body.answer === false && (
                <Chip label="正确答案" size="small" color="success" />
              )}
            </Box>
          }
        />
      </RadioGroup>
    </Box>
  );

  // 渲染匹配题
  const renderMatching = () => (
    <Box>
      {renderHtmlContent(workingQuestion.body.content)}
      
      {interactive && (
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="请输入匹配答案"
          value={userAnswer}
          onChange={(e) => handleAnswerChange(e.target.value)}
          sx={{ mt: 2 }}
        />
      )}
      
      {showAnswer && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="success.main" fontWeight="bold">
            参考答案：
          </Typography>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            '& > span': {
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap'
            }
          }}>
            {typeof workingQuestion.body.answer === 'object'
              ? JSON.stringify(workingQuestion.body.answer, null, 2)
              : renderAnswerContent(workingQuestion.body.answer)}
          </Box>
        </Box>
      )}
    </Box>
  );

  // 渲染子题目
  const renderSubQuestion = (subQ: SubQuestion, index: number) => (
    <Paper key={subQ.id} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
      <Typography variant="subtitle2" gutterBottom>
        {index + 1}. {subQ.content ? renderHtmlContent(subQ.content) : '[题目内容为空]'}
      </Typography>
      
      {subQ.options && (
        <RadioGroup sx={{ mt: 1 }}>
          {subQ.options.map((option, optIndex) => (
            <FormControlLabel
              key={optIndex}
              value={String.fromCharCode(65 + optIndex)}
              control={<Radio size="small" disabled={!interactive} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" component="span">
                    {String.fromCharCode(65 + optIndex)}.
                  </Typography>
                  <div>{renderHtmlContent(option)}</div>
                  {showAnswer && subQ.answer === String.fromCharCode(65 + optIndex) && (
                    <Chip label="正确答案" size="small" color="success" />
                  )}
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 0.5 }}
            />
          ))}
        </RadioGroup>
      )}
      
      {showExplanation && subQ.explanation && (
        <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.contrastText" component="div">
            <strong>解析：</strong>
            {renderHtmlContent(subQ.explanation)}
          </Typography>
        </Box>
      )}
    </Paper>
  );

  // 渲染嵌套题型
  const renderNestedQuestion = () => (
    <Box>
      {/* 材料内容 */}
      {workingQuestion.body.material && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            材料：
          </Typography>
          {renderHtmlContent(workingQuestion.body.material)}
        </Paper>
      )}
      
      {/* 题目说明 */}
      {workingQuestion.body.content && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            题目要求：
          </Typography>
          {renderHtmlContent(workingQuestion.body.content)}
        </Box>
      )}
      
      {/* 子题目 */}
      {workingQuestion.body.subQuestions && workingQuestion.body.subQuestions.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            题目：
          </Typography>
          {workingQuestion.body.subQuestions.map((subQ: any, index: number) => 
            renderSubQuestion(subQ, index)
          )}
        </Box>
      )}
    </Box>
  );

  // 如果数据验证失败，显示错误信息
  if (!validateAndNormalizeQuestion) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
        <Typography variant="h6" gutterBottom>
          题目数据加载失败
        </Typography>
        <Typography variant="body2" color="text.secondary">
          题目数据格式不正确或缺少必要信息，请联系管理员
        </Typography>
        <Typography variant="caption" sx={{ mt: 2, display: 'block', fontFamily: 'monospace', color: 'error.main' }}>
          错误详情: {JSON.stringify({ questionId: question?.id, hasBody: !!question?.body }, null, 2)}
        </Typography>
      </Box>
    );
  }

  const validatedQuestion = validateAndNormalizeQuestion;
  const difficultyInfo = getDifficultyInfo(validatedQuestion.body.difficulty);

  // 为了简化后续代码，将validatedQuestion重新赋值给question
  const workingQuestion = validatedQuestion;

  return (
    <Box>
      {/* 题目信息头部 */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={getQuestionTypeLabel(validatedQuestion.body.type)}
          variant="outlined"
          size="small"
        />
        <Chip 
          label={getSubjectLabel(validatedQuestion.body.subject)} 
          color="primary" 
          variant="outlined" 
          size="small" 
        />
        <Chip 
          label={difficultyInfo.label} 
          color={difficultyInfo.color} 
          variant="outlined" 
          size="small" 
        />
        {validatedQuestion.body.tags && validatedQuestion.body.tags.map((tag: string, index: number) => (
          <Chip 
            key={index} 
            label={tag} 
            variant="outlined" 
            size="small" 
            sx={{ bgcolor: 'grey.100' }}
          />
        ))}
      </Box>

      {/* 题目内容 */}
      <Paper sx={{ p: 3, mb: 2 }}>
        {isNestedQuestion(workingQuestion.body.type) ? (
          renderNestedQuestion()
        ) : (
          <>
            {workingQuestion.body.type === 'SINGLE_CHOICE' && renderSingleChoice()}
            {workingQuestion.body.type === 'MULTIPLE_CHOICE' && renderMultipleChoice()}
            {workingQuestion.body.type === 'FILL_IN_BLANK' && renderFillInBlank()}
            {workingQuestion.body.type === 'TRUE_FALSE' && renderTrueFalse()}
            {workingQuestion.body.type === 'MATCHING' && renderMatching()}
          </>
        )}
      </Paper>

      {/* 答案显示 */}
      {showAnswer && !isNestedQuestion(workingQuestion.body.type) && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.light' }}>
          <Typography variant="body2" color="success.contrastText" fontWeight="bold" component="div">
            正确答案：
            <Box component="span" sx={{
              ml: 1,
              display: 'inline-flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.5,
              '& > span': {
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap'
              }
            }}>
              {(() => {
                const parsedAnswer = parseAnswer(workingQuestion.body.answer);
                return Array.isArray(parsedAnswer)
                  ? parsedAnswer.map((ans, idx) => (
                      <span key={idx}>
                        {renderAnswerContent(ans)}
                        {idx < parsedAnswer.length - 1 && ', '}
                      </span>
                    ))
                  : renderAnswerContent(parsedAnswer);
              })()}
            </Box>
          </Typography>
        </Paper>
      )}

      {/* 解析显示 */}
      {showExplanation && workingQuestion.body.explanation && (
        <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
          <Typography variant="body2" color="info.contrastText" component="div">
            <strong>解析：</strong>
            {renderHtmlContent(workingQuestion.body.explanation)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default QuestionRenderer;
